import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createURLLocalStorage } from '@/utils/storage/url-local-storage';

import { mergePersistedPlannerState } from './persistence/hydration';
import {
  partializePlannerState,
  PLANNER_PREFERENCES_STORAGE_KEY,
  PLANNER_URL_EXCLUDED_PERSIST_KEYS,
  PlannerPersistedState,
} from './persistence/persisted-state';
import { createDatesNavigationSlice } from './slices/dates-navigation.slice';
import { createFiltersSlice } from './slices/filters.slice';
import { createInteractionSlice } from './slices/interaction.slice';
import { createModalsSlice } from './slices/modals.slice';
import { createPlannerDataSlice } from './slices/planner-data.slice';
import { createPreferencesSlice } from './slices/preferences.slice';
import { createQueryRangesSlice } from './slices/query-ranges.slice';
import { createScrollSlice } from './slices/scroll.slice';
import { createUserInfoColumnsSlice } from './slices/user-info-columns.slice';
import { PlannerStore } from './types';

/**
 * The planner store: one zustand store composed from nine concern slices.
 * READ store/PLANNER-STORE.md BEFORE changing anything here — it explains which
 * slice owns what, the behavioral invariants pinned by __tests__/, and where
 * side effects (toasts, DOM scrolls) and persistence are allowed to live.
 */
const usePlannerStore = create<PlannerStore>()(
  persist<PlannerStore, [], [], PlannerPersistedState>(
    (...args) => ({
      ...createDatesNavigationSlice(...args),
      ...createFiltersSlice(...args),
      ...createQueryRangesSlice(...args),
      ...createPlannerDataSlice(...args),
      ...createInteractionSlice(...args),
      ...createModalsSlice(...args),
      ...createScrollSlice(...args),
      ...createUserInfoColumnsSlice(...args),
      ...createPreferencesSlice(...args),
    }),
    {
      name: PLANNER_PREFERENCES_STORAGE_KEY,
      storage: createJSONStorage(() =>
        createURLLocalStorage({ urlExcludeKeys: PLANNER_URL_EXCLUDED_PERSIST_KEYS })
      ),
      partialize: partializePlannerState,
      merge: mergePersistedPlannerState,
    }
  )
);

export { DEFAULT_PLANNER_FILTERS, PLANNER_MEMBER_STATUS_FILTER_KEY } from './slices/filters.slice';
export type {
  EditAssignmentData,
  PlannerStore,
  UserInfoVisibility,
  UserInfoVisibilityProperty,
} from './types';

export default usePlannerStore;
